// apps/web/src/components/programmes/ProgrammeManager.tsx
// Programme management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Programme } from '../../types/programme';
import { apiClient } from '../../lib/apiClient';

export function ProgrammeManager() {
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchProgrammes = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/programmes');
        setProgrammes(response.data.programmes);
      } catch (err) {
        setError('Failed to load programmes');
        console.error('Error loading programmes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProgrammes();
  }, []);

  const handleCreateProgramme = async (programmeData: any) => {
    try {
      const response = await apiClient.post('/api/v1/programmes', programmeData);
      setProgrammes([response.data, ...programmes]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create programme');
      console.error('Error creating programme:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading programmes...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="programme-manager">
      <div className="header">
        <h2>Programme Management</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Programme'}
        </button>
      </div>

      {showCreateForm && (
        <ProgrammeForm 
          onSubmit={handleCreateProgramme} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="programmes-grid">
        {programmes.map(programme => (
          <ProgrammeCard key={programme.id} programme={programme} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function ProgrammeCard({ programme }: { programme: Programme }) {
  return (
    <div className="programme-card">
      <div className="programme-header">
        <h3>{programme.name}</h3>
        <span className="programme-status">{programme.status}</span>
      </div>
      <p>{programme.description || 'No description'}</p>
      <p><strong>Brand:</strong> {programme.brandId.substring(0, 8)}...</p>
      <p><strong>Created:</strong> {new Date(programme.createdAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Edit</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function ProgrammeForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    brandId: '',
    startDate: '',
    endDate: '',
    status: 'ACTIVE'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="programme-form">
      <h3>Create New Programme</h3>
      <input
        type="text"
        placeholder="Programme Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <input
        type="text"
        placeholder="Brand ID"
        value={formData.brandId}
        onChange={(e) => setFormData({...formData, brandId: e.target.value})}
        required
      />
      <input
        type="date"
        value={formData.startDate}
        onChange={(e) => setFormData({...formData, startDate: e.target.value})}
      />
      <input
        type="date"
        value={formData.endDate}
        onChange={(e) => setFormData({...formData, endDate: e.target.value})}
      />
      <select
        value={formData.status}
        onChange={(e) => setFormData({...formData, status: e.target.value})}
      >
        <option value="ACTIVE">Active</option>
        <option value="DRAFT">Draft</option>
        <option value="ARCHIVED">Archived</option>
        <option value="CANCELLED">Cancelled</option>
      </select>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Programme</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}